import { CommonGridObject, FilterInput, GridInput } from '@it2go/data-grid';
import { effect, inject, Injectable, InjectionToken, signal, untracked } from '@angular/core';
import { gqlApi } from '../../modules/shared/shared.global-service';
import { currentUserId, selectedOrganizationId } from '../current-user-id';
import { isOffline } from '../../utils/connection.service';
import { clearToRemoveObjects, getNestedValues, setNestedValues } from '../../utils/object.utils';
import { Subject } from 'rxjs';
import { AppIndexedDb } from './db';
import { debounceTime } from 'rxjs/operators';
import { randomNumber } from '../../utils/int.utils';
import { CommonDb } from './common.db';
import { removeTypename } from '../../modules/shared/utils/object.utils';
import { OrganizationGlobalStore } from '../organization.global-store';
import { OrganizationSettingTypeEnum } from '@libs/types/src/organization/enum/organization-setting-type.enum';
import { featureOn } from '../../feature-flag';
import { userProfilePreferences } from '../user-profile-preferences';

export const JOIN_PREFIX = '$.';

export const OFFLINE_DB_TOKEN = new InjectionToken<ObjectDb<any>>('ObjectDb');

@Injectable({ providedIn: 'root' })
export abstract class ObjectDb<T> extends AppIndexedDb<T> {

    protected readonly organizationGlobalStore = inject(OrganizationGlobalStore);

    /**
     * Data fragment for fetched and stored values
     */
    protected abstract syncFragment: string;

    /**
     * Named of query method within gqlApi -> cannot import method directly as it is initialized later on and thus would result into undefined
     * f.e.: gqlApi.getPersons -> 'getPersons'
     */
    protected abstract gqlMethod: string;

    /**
     * Data is filtered and stored based on currentOrganizationId -> which is usually not part of query itself, but provided in req header
     */
    protected splitByOrganizations: boolean = false;

    /**
     * Defaults to 'updatedAt' to make incremental updates, if set to null, always makes full sync
     */
    protected checkField: string | null = 'updatedAt';

    /**
     * If Db should be wiped out for different users
     */
    protected refreshOnUserChange: boolean = true;

    /**
     * comma separated index keys, see https://dexie.org/docs/Version/Version.stores()
     */
    protected indices: string[] = ['id'];
    protected columnMap: Record<string, string | null> = {};

    /**
     * Join fields mapper f.e.
     * Table '$.folder' is joined by 'folders.id'
     */
    protected joins: Record<string, string> = {};

    /**
     * Joined tables stored as key: collection
     * Where 'key' must match with $.key from column map and tableName
     */
    protected joinedTables: Record<string, IDBObjectStore> = {};

    public isDownloading = signal(false);
    protected isDownloadingSubject = new Subject<boolean>();

    protected pageSize: number = 100;

    constructor() {
        super();
        // Delay to avoid label blinking
        this.isDownloadingSubject
            .pipe(debounceTime(1_000))
            .subscribe((it) => this.isDownloading.set(it))
        ;

        effect(async () => { // Force update should userId change
            if (this.refreshOnUserChange) {
                currentUserId();
                if (!this.db) return;
                if (await this.clearOnUserChange()) {
                    await this.paginate();
                }
            }
        });
    }

    public async filter(input: GridInput): Promise<CommonGridObject> {
        let items: T[] = [];
        const store = this.getStore();
        // TODO indices cannot be easily used as only 1 can be queried at a time - for now, only use idx_organization if exists to simplify code
        if (this.splitByOrganizations) {
            items = await this.promise(
                store.index('organizationId').getAll(selectedOrganizationId()));
        } else {
            items = await this.promise(store.getAll());
        }

        // Fetch joined data
        const joins = Object.entries(this.joins);
        await Promise.all(joins.flatMap(async ([tableName, keyPath]) => {
            items = await Promise.all(items.map(async (it: any) => {
                let data = JSON.parse(JSON.stringify(it));
                const keys = getNestedValues(it, keyPath);

                const joinStore = new CommonDb(this.db, tableName);
                const values = await joinStore.getMany(keys);

                data = setNestedValues(data, keyPath, values);

                return data;
            }));
        }));

        clearToRemoveObjects(items);
        items = this.checkFilters(items, input);

        // TODO sort
        // db.where('age').sortBy('name')

        const total = items?.length || 0;
        const itemsPerPage = input.paging?.itemsPerPage ?? 10;
        const lastPage = Math.max(Math.ceil(total / itemsPerPage), 1);
        const page = Math.min(input.paging?.page ?? 1, lastPage);

        return {
            items: items.slice((page - 1) * itemsPerPage, page * itemsPerPage),
            paging: {
                page: input.paging?.page || 1,
                itemsPerPage: input.paging?.itemsPerPage || 10,
                counted: true,
                total,
                nextPage: Math.min(page + 1, lastPage),
                lastPage,
                previousPage: Math.max(page - 1, 1),
                hasNextPage: page < lastPage,
                hasPreviousPage: page > 1,
            },
        };
    }

    public async refresh(clear: boolean = true): Promise<void> {
        clear && await this.clear();
        await this.paginate();
    }

    /**
     * Creates a store - MUST be called within onupgradeneeded event
     */
    public initializeStore(db: IDBDatabase): void {
        const store = db.createObjectStore(this.tableName, {
            keyPath: this.indices[0],
        });
        this.indices.slice(1).forEach((it) => {
            store.createIndex(it, it);
        });
    }

    public setDatabase(db: IDBDatabase): void {
        this.db = db;
        this.splitByOrganizations = this.indices.includes('organizationId');
        // Delayed start to let app start up first - range to spread the load
        setTimeout(() => this.startSync(), randomNumber(20_000, 25_000));
    }

    public override async clear(ignoreIfUserAgnostic: boolean = false): Promise<void> {
        if (ignoreIfUserAgnostic && !this.refreshOnUserChange) return;

        return super.clear();
    }

    protected postProcessSyncItems(items: T[]): T[] {
        return items.map((it) => removeTypename(it));
    }

    protected parseJoinKey(key: string): [string, string] {
        if (!key.startsWith(JOIN_PREFIX)) return ['', key];

        const parseKey = key.split('.', 2);
        const tableName = parseKey[1];
        key = key.substring(tableName.length + JOIN_PREFIX.length + 1); // Skips prefix $.tableName.

        let joinPath = this.joins[tableName];
        joinPath = joinPath.substring(0, joinPath.lastIndexOf('.'));
        key = `${joinPath}.${key}`;

        return [tableName, key];
    }

    protected checkFilters(items: T[], input: GridInput): T[] {
        return items.filter((it) => {
            let isOk = true;
            for (const ors of (input.filter || [])) {
                let isOrOk = false;
                for (const or of ors) {
                    const _key = this.columnMap[or.column] || or.column;
                    if (_key === null) continue;

                    if (_key?.startsWith('custom::')) {
                        isOrOk ||= this.customFilter(_key.substring('custom::'.length), it, or);
                        if (isOrOk) break;
                        continue;
                    }

                    const [_table, key] = this.parseJoinKey(_key);
                    let values: any;
                    values = getNestedValues(it, key);
                    isOrOk ||= this.checkValue(values, or);
                    if (isOrOk) break;
                }
                isOk &&= isOrOk;
                if (!isOk) return false;
            }

            return isOk;
        });
    }

    protected checkValue(values: any[], filter: FilterInput): boolean {
        const v0 = filter.value[0];
        switch (filter.operator.toUpperCase()) {
            case 'EQ':
                return values.some((i: any) => this.compareEq(i, v0));
            case 'NEQ':
                return !values.some((i: any) => this.compareEq(i, v0));
            case 'IN':
                return values.some((i: any) => filter.value.some((j) => this.compareEq(i, j)));
            case 'NIN':
                return values.length > 0 && values.every((i: any) => !filter.value.some((j) => this.compareEq(i, j)));
            case 'LT':
                return values.some((i: any) => i < v0);
            case 'LTE':
                return values.some((i: any) => i <= v0);
            case 'GT':
                return values.some((i: any) => i > v0);
            case 'GTE':
                return values.some((i: any) => i >= v0);
            case 'EMPTY':
                return values.some((i: any) => !i);
            case 'NEMPTY':
                return values.some((i: any) => !!i);
            default:
                console.error('Unimplemented offline db filter', filter);
                throw new Error(`'Unimplemented offline db filter [${filter}]'`);
        }
    }

    protected compareEq(dbValue: any, filterValue: any): boolean {
        return String(dbValue) == filterValue;
    }

    protected startSync(): void {
        if (!featureOn.OFFLINE_MODE) return;

        const sync = (): void => {
            if (!userProfilePreferences().downloadOfflineDataRegularly) return;
            this.paginate();
        };
        sync();
        setInterval(sync, 15 * 60_000);
    }

    /**
     * Synchronize data from BE into local db, updates dataSchema to lastUpdated value if checkField is present (default is updatedAt)
     */
    protected async paginate(): Promise<void> {
        if (isOffline() || !currentUserId()) return;
        const setting = this.organizationGlobalStore.settings().find((it) => it.key == OrganizationSettingTypeEnum.HEALTHCARE_SOCIAL_SETTINGS);
        if (!setting?.active) return;

        if (untracked(() => this.isDownloading())) return;
        this.isDownloading.set(true);

        try {
            const syncDate = new Date();
            const filter: GridInput = {
                filter: [],
                paging: {
                    page: 0,
                    itemsPerPage: this.pageSize,
                },
                audit: false,
            };

            const dataSchema = await this.getSchema();

            let lastUpdate = dataSchema?.value;
            if (this.checkField) {
                if (lastUpdate) {
                    filter.filter?.push([{
                        column: this.checkField,
                        operator: 'GT',
                        value: [lastUpdate],
                    }]);
                }

                filter.sorter = [{ column: this.checkField, direction: 'ASC' }];
            }

            let res;
            while (!isOffline()) {
                filter.paging!.page!++;
                // Cannot add callable directly as it's not initialized in time
                res = await (gqlApi as any)[this.gqlMethod](
                    filter,
                    this.syncFragment,
                    { context: { suppressGlobalErrors: true } },
                );

                if (this.checkField) {
                    lastUpdate = res?.items?.[0]?.[this.checkField] || lastUpdate;
                }

                const parsed = this.postProcessSyncItems(res?.items || []);

                const store = this.getStore('readwrite');
                await Promise.all(parsed.map((it) => this.put(it, store)));
                if ((res?.items?.length || 0) < this.pageSize) break;
            }

            await this.updateSchema(syncDate);
        } catch (e) {
            console.error(e, 'ObjectDb', this.tableName);
        } finally {
            this.isDownloadingSubject.next(false);
        }
    }

    /**
     * Used for more complex filters - similar to overriding basic filter in grid.ts
     */
    protected customFilter(key: string, item: T, filter: FilterInput): boolean {
        return false;
    }

}
